import axios from 'axios'
import store from '../store'

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Добавляем токен к каждому запросу (fallback на localStorage на случай гонки при переходе после логина)
api.interceptors.request.use(config => {
  const token = store.state.auth.token || localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// --- Автоматическое продление сессии по refresh-токену портала ---

// Общий promise для параллельных 401: refresh выполняется один раз
let refreshPromise = null

/**
 * Пробует обновить app-JWT по сохранённому refresh-токену.
 * Возвращает новый access_token или null при неудаче.
 */
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return null

  try {
    const response = await axios.post(
      `${api.defaults.baseURL}/auth/keycloak/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    const data = response.data
    if (!data || !data.access_token) return null

    store.commit('SET_AUTH', {
      token: data.access_token,
      user: data.user || store.state.auth.user
    })
    return data.access_token
  } catch (e) {
    // refresh-токен недействителен/просрочен — сессия не восстанавливается
    return null
  }
}

// Обработка ошибок авторизации
api.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status
    const config = error.config || {}

    // 401/422 — токен истёк или невалиден: пробуем refresh и повторяем запрос
    const isAuthEndpoint = (config.url || '').includes('/auth/keycloak/refresh')
    if ((status === 401 || status === 422) && !config._retried && !isAuthEndpoint) {
      config._retried = true

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null })
      }
      const newToken = await refreshPromise

      if (newToken) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${newToken}`
        return api(config)
      }

      // Refresh не удался — разлогиниваем
      store.dispatch('logout')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default {
// Авторизация
  login(username, password, serialKey) {
    return api.post('/auth/login', { username, password, serial_key: serialKey })
  },

  getKeycloakConfig() {
    return api.get('/auth/keycloak/config')
  },

  keycloakLogin(code, codeVerifier, redirectUri) {
    return api.post('/auth/keycloak', {
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri
    })
  },
  
  getCurrentUser() {
    return api.get('/auth/me')
  },

  // Запись серийного ключа текущим пользователем (ключ не влияет на выдачу токена)
  setSerialKey(serialKey) {
    return api.post('/auth/set-serial-key', { serial_key: serialKey })
  },

  
  // Чертежи
  getDrawings(params) {
    return api.get('/drawings/list', { params })
  },
  
  uploadDrawing(file, name) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name || file.name)
    return api.post('/drawings/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  
  downloadDrawing(uid) {
    return api.get(`/drawings/download/${uid}`, { responseType: 'blob' })
  },
  
  /** @param {number} withWalls 1 — режим eLinkByIds (плагин); иначе eLinkByTextLabels */
  getFileDwg(docGuid, withWalls = 0, fromPortal = false) {
    const params = { doc_guid: docGuid, with_walls: withWalls }
    if (fromPortal) {
      params.from_portal = '1'
    }
    return api.get('/getfiledwg/', {
      params,
      responseType: 'blob'
    })
  },
  
  getOwners() {
    return api.get('/drawings/owners')
  },
  
  updateDrawing(uid, data) {
    return api.put(`/drawings/${uid}`, data)
  },
  
  deleteDrawing(uid) {
    return api.delete(`/drawings/${uid}`)
  },
  
  getAllUsers() {
    return api.get('/users/list')
  },
  
  // Пользователи
  getUsers() {
    return api.get('/users/list')
  },
  
  getUser(uid) {
    return api.get(`/users/${uid}`)
  },
  
  createUser(data) {
    return api.post('/users/', data)
  },
  
  updateUser(uid, data) {
    return api.put(`/users/${uid}`, data)
  },
  
  assignGroupToUser(userUid, groupUid, replaceAll = false) {
    return api.post(`/users/${userUid}/groups`, { group_uid: groupUid, replace_all: replaceAll })
  },
  
  removeGroupFromUser(userUid, groupUid) {
    return api.delete(`/users/${userUid}/groups/${groupUid}`)
  },
  
  // Группы
  getMyGroups() {
    return api.get('/groups/my-groups')
  },
  
  getUserGroups(userUid) {
    return api.get(`/groups/user/${userUid}`)
  },
  
  getGroupsTree() {
    return api.get('/groups/tree')
  },
  
  createGroup(name, parentUid) {
    return api.post('/groups/', { name, parent_uid: parentUid || null })
  },
  
  updateGroup(uid, name) {
    return api.put(`/groups/${uid}`, { name })
  },
  
  deleteGroup(uid) {
    return api.delete(`/groups/${uid}`)
  },
  
  // Отчеты
  generateReport(startDate, endDate) {
    return api.post('/reports/generate', {
      start_date: startDate,
      end_date: endDate
    }, { responseType: 'blob' })
  },

  generateActivitiesReport(startDate, endDate, userUid = null) {
    return api.post('/reports/activities/generate', {
      start_date: startDate,
      end_date: endDate,
      user_uid: userUid
    }, { responseType: 'blob' })
  },

  createActivity(data) {
    return api.post('/reports/activities', data)
  },
  
  // Поддержка
  getSupportContacts() {
    return api.get('/support/contacts')
  },
  
  createSupportRequest(subject, message) {
    return api.post('/support/request', { subject, message })
  },
  
  // Дэшборд
  getDashboardInfo() {
    return api.get('/dashboard/info')
  },
  
  // Настройки
  getSettings(params) {
    return api.get('/settings/list', { params })
  },
  
  getSettingsGroups() {
    return api.get('/settings/groups')
  },
  
  getSetting(id) {
    return api.get(`/settings/${id}`)
  },
  
  createSetting(data) {
    return api.post('/settings/', data)
  },
  
  updateSetting(id, data) {
    return api.put(`/settings/${id}`, data)
  },
  
  deleteSetting(id) {
    return api.delete(`/settings/${id}`)
  }
}

